import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  CircularProgress,
  IconButton,
  Tooltip,
  InputAdornment,
  Stack,
  Pagination,
} from "@mui/material";
import {
  ImageNotSupported,
  Add,
  Delete,
  Save,
  Search,
  BrokenImage,
  CheckCircle,
  ErrorOutline,
  Refresh,
} from "@mui/icons-material";
import { updateProduct } from "../../api/api";
import api from "../../api/api";

const PAGE_SIZE = 20;

export default function ImageRecovery() {
  const [imageFilter, setImageFilter] = useState("no-image");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ page: 1, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [urlDrafts, setUrlDrafts] = useState({});
  const [saveStatus, setSaveStatus] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);

  const hasImagesParam =
    imageFilter === "has-image" ? "true" : imageFilter === "no-image" ? "false" : undefined;

  const loadPage = useCallback(async (p, q, hasImages) => {
    setLoading(true);
    try {
      const params = { page: p, limit: PAGE_SIZE, search: q, active: "false" };
      if (hasImages !== undefined) params.hasImages = hasImages;
      const res = await api.get("/api/products", {
        params,
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      });
      const incoming = res.data?.data || [];
      const m = res.data?.meta || { page: p, pages: 1, total: incoming.length };
      setProducts(incoming);
      setMeta(m);
      setUrlDrafts((prev) => {
        const next = { ...prev };
        incoming.forEach((prod) => {
          if (!next[prod._id]) {
            next[prod._id] = prod.images?.length ? [...prod.images, ""] : [""];
          }
        });
        return next;
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reload when filter or search changes
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
    loadPage(1, search, hasImagesParam);
  }, [imageFilter, search]);

  // Navigate pages
  useEffect(() => {
    loadPage(page, search, hasImagesParam);
  }, [page]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  const visible = products;
  const noImageCount = imageFilter === "no-image" ? meta.total : null;
  const hasImageCount = imageFilter === "has-image" ? meta.total : null;

  // --- Draft helpers ---
  const getCleanUrls = (productId) =>
    (urlDrafts[productId] || []).filter((u) => u.trim() !== "");

  const isDirty = (product) => {
    const draft = getCleanUrls(product._id);
    const current = product.images || [];
    return JSON.stringify(draft) !== JSON.stringify(current);
  };

  const setUrl = (productId, index, value) => {
    setUrlDrafts((prev) => {
      const urls = [...(prev[productId] || [""])];
      urls[index] = value;
      return { ...prev, [productId]: urls };
    });
  };

  const addUrl = (productId) => {
    setUrlDrafts((prev) => ({
      ...prev,
      [productId]: [...(prev[productId] || [""]), ""],
    }));
  };

  const removeUrl = (productId, index) => {
    setUrlDrafts((prev) => {
      const urls = (prev[productId] || [""]).filter((_, i) => i !== index);
      return { ...prev, [productId]: urls.length ? urls : [""] };
    });
  };

  function applyUpdate(id, urls) {
    setProducts((prev) =>
      prev.map((p) => (p._id === id ? { ...p, images: urls } : p))
    );
  }

  // --- Save ---
  async function saveOne(product) {
    const urls = getCleanUrls(product._id);
    setSaveStatus((prev) => ({ ...prev, [product._id]: "saving" }));
    try {
      await updateProduct(product._id, { images: urls });
      applyUpdate(product._id, urls);
      setSaveStatus((prev) => ({ ...prev, [product._id]: "saved" }));
      setTimeout(
        () =>
          setSaveStatus((prev) => {
            const n = { ...prev };
            delete n[product._id];
            return n;
          }),
        2500
      );
    } catch {
      setSaveStatus((prev) => ({ ...prev, [product._id]: "error" }));
    }
  }

  async function saveSelected() {
    const toSave = visible.filter((p) => selected.has(p._id) && isDirty(p));
    if (!toSave.length) return;
    setBulkSaving(true);
    await Promise.allSettled(toSave.map((p) => saveOne(p)));
    setBulkSaving(false);
    setSelected(new Set());
  }

  // --- Select ---
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === visible.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visible.map((p) => p._id)));
    }
  };

  const dirtyCount = visible.filter((p) => isDirty(p)).length;
  const selectedDirtyCount = visible.filter(
    (p) => selected.has(p._id) && isDirty(p)
  ).length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ color: "#1e293b", mb: 0.5 }}
        >
          🖼️ Image Recovery
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Re-attach Cloudinary URLs to products that lost their images after the
          database recovery.
        </Typography>
      </Box>

      {/* Stats */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <StatCard label="Total Products" value={meta.total} color="#667eea" />
        {noImageCount != null && (
          <StatCard label="Missing Images" value={noImageCount} color="#ef4444" />
        )}
        {hasImageCount != null && (
          <StatCard label="Have Images" value={hasImageCount} color="#22c55e" />
        )}
        <StatCard label="Unsaved Changes" value={dirtyCount} color="#f59e0b" />
      </Box>

      {/* Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: "1.5px solid #e2e8f0",
          borderRadius: 3,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          placeholder="Search products..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220 }}
        />

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {[
            {
              key: "no-image",
              label: `No Image${imageFilter === "no-image" ? ` (${meta.total})` : ""}`,
              icon: <ImageNotSupported />,
            },
            {
              key: "has-image",
              label: `Has Images${imageFilter === "has-image" ? ` (${meta.total})` : ""}`,
            },
            { key: "all", label: "All" },
          ].map(({ key, label, icon }) => (
            <Button
              key={key}
              size="small"
              variant={imageFilter === key ? "contained" : "outlined"}
              onClick={() => setImageFilter(key)}
              startIcon={icon}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                ...(imageFilter === key && {
                  bgcolor: "#667eea",
                  "&:hover": { bgcolor: "#5a6fd6" },
                }),
              }}
            >
              {label}
            </Button>
          ))}
        </Box>

        <Box
          sx={{ ml: "auto", display: "flex", gap: 1.5, alignItems: "center" }}
        >
          <Tooltip title="Reload">
            <IconButton
              size="small"
              onClick={() => loadPage(page, search, hasImagesParam)}
              disabled={loading}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>

          {selected.size > 0 && (
            <Button
              variant="contained"
              size="small"
              onClick={saveSelected}
              disabled={bulkSaving || selectedDirtyCount === 0}
              startIcon={
                bulkSaving ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <Save />
                )
              }
              sx={{
                borderRadius: 2,
                textTransform: "none",
                bgcolor: "#667eea",
                "&:hover": { bgcolor: "#5a6fd6" },
              }}
            >
              {bulkSaving
                ? "Saving..."
                : `Save ${selected.size} Selected${
                    selectedDirtyCount !== selected.size
                      ? ` (${selectedDirtyCount} changed)`
                      : ""
                  }`}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : visible.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            border: "1.5px dashed #e2e8f0",
            borderRadius: 3,
          }}
        >
          <Typography color="text.secondary" sx={{ fontSize: 15 }}>
            {imageFilter === "no-image"
              ? "🎉 All products on this page have images!"
              : imageFilter === "has-image"
              ? "No products with images on this page."
              : "No products found."}
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: "1.5px solid #e2e8f0",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={
                      selected.size > 0 && selected.size < visible.length
                    }
                    checked={
                      visible.length > 0 && selected.size === visible.length
                    }
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 700, fontSize: 12, color: "#475569" }}
                >
                  Product
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 700, fontSize: 12, color: "#475569" }}
                >
                  Category
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 700, fontSize: 12, color: "#475569" }}
                >
                  Current Images
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    color: "#475569",
                    minWidth: 360,
                  }}
                >
                  Cloudinary URLs
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 700, fontSize: 12, color: "#475569" }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((product) => {
                const drafts = urlDrafts[product._id] || [""];
                const status = saveStatus[product._id];
                const dirty = isDirty(product);

                return (
                  <TableRow
                    key={product._id}
                    sx={{
                      "&:hover": { bgcolor: "#f8fafc" },
                      bgcolor: selected.has(product._id)
                        ? "rgba(102,126,234,0.04)"
                        : "white",
                      borderLeft: dirty
                        ? "3px solid #f59e0b"
                        : "3px solid transparent",
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected.has(product._id)}
                        onChange={() => toggleSelect(product._id)}
                      />
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <Avatar
                          src={product.images?.[0]}
                          variant="rounded"
                          sx={{ width: 40, height: 40, bgcolor: "#f1f5f9" }}
                        >
                          <BrokenImage
                            sx={{ fontSize: 18, color: "#94a3b8" }}
                          />
                        </Avatar>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#1e293b",
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {product.title}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 11, color: "#64748b" }}
                          >
                            KSh {product.price?.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={product.category?.name || "—"}
                        size="small"
                        sx={{ fontSize: 11, height: 20 }}
                      />
                    </TableCell>

                    <TableCell>
                      {product.images?.length ? (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          {product.images.slice(0, 3).map((url, i) => (
                            <Avatar
                              key={i}
                              src={url}
                              variant="rounded"
                              sx={{ width: 32, height: 32 }}
                            />
                          ))}
                          {product.images.length > 3 && (
                            <Chip
                              label={`+${product.images.length - 3}`}
                              size="small"
                              sx={{ height: 32, fontSize: 10 }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Chip
                          label="No images"
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ fontSize: 11, height: 20 }}
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      <Stack spacing={0.8}>
                        {drafts.map((url, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              alignItems: "center",
                            }}
                          >
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="https://res.cloudinary.com/..."
                              value={url}
                              onChange={(e) =>
                                setUrl(product._id, i, e.target.value)
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  fontSize: 11.5,
                                  borderRadius: 1.5,
                                },
                              }}
                            />
                            {drafts.length > 1 && (
                              <IconButton
                                size="small"
                                onClick={() => removeUrl(product._id, i)}
                                sx={{ color: "#ef4444", flexShrink: 0 }}
                              >
                                <Delete sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                        <Button
                          size="small"
                          startIcon={<Add sx={{ fontSize: 14 }} />}
                          onClick={() => addUrl(product._id)}
                          sx={{
                            fontSize: 11,
                            textTransform: "none",
                            color: "#667eea",
                            justifyContent: "flex-start",
                            p: 0,
                            pl: 0.5,
                            minWidth: 0,
                            "&:hover": {
                              bgcolor: "transparent",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Add another URL
                        </Button>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          alignItems: "flex-start",
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => saveOne(product)}
                          disabled={status === "saving" || !dirty}
                          startIcon={
                            status === "saving" ? (
                              <CircularProgress size={12} color="inherit" />
                            ) : status === "saved" ? (
                              <CheckCircle sx={{ fontSize: 14 }} />
                            ) : status === "error" ? (
                              <ErrorOutline sx={{ fontSize: 14 }} />
                            ) : (
                              <Save sx={{ fontSize: 14 }} />
                            )
                          }
                          sx={{
                            borderRadius: 1.5,
                            fontSize: 11,
                            textTransform: "none",
                            minWidth: 80,
                            bgcolor:
                              status === "saved"
                                ? "#22c55e"
                                : status === "error"
                                ? "#ef4444"
                                : "#667eea",
                            "&:hover": {
                              bgcolor:
                                status === "saved"
                                  ? "#16a34a"
                                  : status === "error"
                                  ? "#dc2626"
                                  : "#5a6fd6",
                            },
                            "&.Mui-disabled": {
                              bgcolor: "#e2e8f0",
                              color: "#94a3b8",
                            },
                          }}
                        >
                          {status === "saving"
                            ? "Saving..."
                            : status === "saved"
                            ? "Saved!"
                            : status === "error"
                            ? "Retry"
                            : "Save"}
                        </Button>
                        {dirty && !status && (
                          <Typography
                            sx={{
                              fontSize: 10,
                              color: "#f59e0b",
                              fontWeight: 700,
                            }}
                          >
                            unsaved
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {meta.pages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={meta.pages}
            page={page}
            onChange={(_, val) => {
              setPage(val);
              setSelected(new Set());
            }}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
}

function StatCard({ label, value, sublabel, color }) {
  return (
    <Paper
      elevation={0}
      sx={{
        px: 2.5,
        py: 1.5,
        border: "1.5px solid #e2e8f0",
        borderRadius: 2.5,
        minWidth: 140,
      }}
    >
      <Typography
        sx={{ fontSize: 24, fontWeight: 900, color, lineHeight: 1.2 }}
      >
        {value}
      </Typography>
      <Typography
        sx={{ fontSize: 11.5, color: "#64748b", fontWeight: 600, mt: 0.3 }}
      >
        {label}
        {sublabel && (
          <Box component="span" sx={{ color: "#94a3b8", fontWeight: 400 }}>
            {" "}
            {sublabel}
          </Box>
        )}
      </Typography>
    </Paper>
  );
}
